import { useGameOverlayPause } from './useGameOverlayPause';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, Send, X, Gamepad2, Ellipsis, ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import { Fragment } from 'react';
import { api, json } from '../api';
import { usePlatform } from '../store';
import { UserAvatar } from './UserAvatar';
import './direct-messages.css';
import './social-themes.css';
import { NoticeCard } from './NoticeCard';
type Conversation = {
  id: string;
  name: string;
  avatarUrl: string;
  canSend: boolean;
  unread: number;
  last: { seq: number; sender: string; body: string; createdAt: string } | null;
};
type Summary = { conversations: Conversation[]; unreadCount: number; roomId: string | null };
type Message = { seq: number; id: string; sender: string; body: string; createdAt: string };
type Invitation = {
  id: string;
  sender: string;
  recipient: string;
  createdAt: string;
  status: string;
  name: string;
  coverImage: string;
  players: number | null;
  capacity: number | null;
};
type History = {
  invitations: Invitation[];
  messages: Message[];
  next: number | null;
  canSend: boolean;
};
const Context = createContext<{
  summary: Summary;
  open: (id: string) => void;
  refresh: () => Promise<void>;
} | null>(null);
export const useDirectMessages = () => useContext(Context)!;
export function DirectMessagesProvider({ children }: { children: ReactNode }) {
  const { data } = usePlatform();
  return (
    <DirectMessagesSession key={data?.user?.id || 'guest'} userId={data?.user?.id}>
      {children}
    </DirectMessagesSession>
  );
}
function DirectMessagesSession({ children, userId }: { children: ReactNode; userId?: string }) {
  const navigate = useNavigate(),
    location = useLocation();
  const routePeer = location.pathname.startsWith('/messages/')
    ? decodeURIComponent(location.pathname.slice('/messages/'.length))
    : null;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const openChat = (id: string) => {
    setHint(null);
    if (window.matchMedia('(max-width: 600px)').matches && !pathRef.current.startsWith('/play/')) {
      setPeer(null);
      navigate(`/messages/${encodeURIComponent(id)}`, { state: { fromChatEntry: true } });
    } else setPeer(id);
    window.dispatchEvent(new Event('moyu:chat-opened'));
  };

  const [summary, setSummary] = useState<Summary>({
    conversations: [],
    unreadCount: 0,
    roomId: null,
  });
  const [peer, setPeer] = useState<string | null>(null),
    [hint, setHint] = useState<Conversation | null>(null);
  const seen = useRef(new Set<number>()),
    alive = useRef(true),
    activePeer = useRef(peer);
  activePeer.current = routePeer || peer;
  const refresh = async () => {
    if (!userId) return;
    const next = await api<Summary>('/messages');
    if (!alive.current) return;
    const fresh = next.conversations.find(
      (c) =>
        c.unread &&
        c.last &&
        c.last.sender !== userId &&
        !seen.current.has(c.last.seq) &&
        c.id !== activePeer.current,
    );
    for (const c of next.conversations) if (c.last) seen.current.add(c.last.seq);
    setSummary(next);
    if (fresh) setHint(fresh);
  };
  useEffect(() => {
    alive.current = true;
    let pending = false;
    const read = () => {
      if (document.hidden || pending) return;
      pending = true;
      void refresh()
        .catch(() => {})
        .finally(() => {
          pending = false;
        });
    };
    read();
    const timer = setInterval(read, 5000);
    window.addEventListener('focus', read);
    window.addEventListener('moyu:social-changed', read);
    const openEvent = (e: Event) => {
      openChat((e as CustomEvent<string>).detail);
      read();
    };
    window.addEventListener('moyu:open-chat', openEvent);
    return () => {
      alive.current = false;
      clearInterval(timer);
      window.removeEventListener('focus', read);
      window.removeEventListener('moyu:social-changed', read);
      window.removeEventListener('moyu:open-chat', openEvent);
    };
  }, [userId]);
  const open = openChat;
  const person = summary.conversations.find((c) => c.id === peer);
  const gameChat = location.pathname.startsWith('/play/');
  useEffect(() => {
    setPeer(null);
  }, [location.pathname]);
  return (
    <Context.Provider value={{ summary, open, refresh }}>
      {children}
      {userId &&
        !routePeer &&
        peer &&
        person &&
        createPortal(
          <div className={gameChat ? 'dm-game-overlay' : undefined}>
            <Chat
              key={peer}
              person={person}
              userId={userId}
              roomId={summary.roomId}
              close={() => setPeer(null)}
              inGame={gameChat}
            />
          </div>,
          document.body,
        )}
      {userId &&
        hint &&
        hint.id !== peer &&
        summary.conversations.some((c) => c.id === hint.id && c.unread > 0) &&
        createPortal(
          <NoticeCard
            className="notice-direct"
            name={hint.name}
            avatar={hint.avatarUrl}
            meta="好友私信 · 新消息"
            onClose={() => setHint(null)}
            actions={
              <button className="notice-primary" onClick={() => open(hint.id)}>
                回复
              </button>
            }
          >
            <p>{hint.last?.body}</p>
          </NoticeCard>,
          document.body,
        )}
    </Context.Provider>
  );
}
export function DirectMessagesList() {
  const { summary, open, refresh } = useDirectMessages();
  const [error, setError] = useState('');
  useEffect(() => {
    void refresh().catch((e) => setError(e.message));
  }, []);
  return (
    <div className="dm-conversations">
      <div className="dm-list-heading">
        <strong>好友私信</strong>
        <span>聊两句，一起玩</span>
      </div>
      {error && <p role="alert">{error}</p>}
      {!summary.conversations.length ? (
        <div className="notifications-empty">
          <MessageCircle size={32} />
          <strong>还没有会话</strong>
          <p>添加好友，就能聊聊想玩的游戏</p>
        </div>
      ) : (
        summary.conversations.map((c) => (
          <button className="dm-conversation" key={c.id} onClick={() => open(c.id)}>
            <UserAvatar src={c.avatarUrl} size={44} />
            <span>
              <strong>{c.name}</strong>
              <small>{c.last?.body || (c.canSend ? '打个招呼吧' : '暂时无法发送私信')}</small>
            </span>
            {c.unread > 0 && <b>{c.unread > 99 ? '99+' : c.unread}</b>}
          </button>
        ))
      )}
    </div>
  );
}
export function DirectMessagesPage() {
  const { data } = usePlatform();
  const { peer } = useParams();
  const { summary, refresh } = useDirectMessages();
  const navigate = useNavigate(),
    location = useLocation();
  const [ready, setReady] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    void refresh()
      .catch((e) => {
        if (live) setError(e.message);
      })
      .finally(() => {
        if (live) setReady(true);
      });
    return () => {
      live = false;
    };
  }, [peer, data?.user?.id]);
  const back = () =>
    location.state?.fromChatEntry ? navigate(-1) : navigate('/', { replace: true });
  const person = summary.conversations.find((c) => c.id === peer);
  if (data && !data.user) return <Navigate to="/login" replace />;
  return (
    <main className="dm-page">
      {data?.user && person ? (
        <Chat
          key={peer}
          person={person}
          userId={data.user.id}
          roomId={summary.roomId}
          close={back}
          fullPage
        />
      ) : (
        <div className="dm-page-loading">
          <button onClick={back}>
            <ArrowLeft size={20} />
            返回
          </button>
          <p>{error || (!ready ? '正在打开聊天…' : '会话不存在或已不可用')}</p>
        </div>
      )}
    </main>
  );
}
function Chat({
  person,
  userId,
  roomId,
  close,
  fullPage = false,
  inGame = false,
}: {
  person: Conversation;
  userId: string;
  roomId: string | null;
  close: () => void;
  fullPage?: boolean;
  inGame?: boolean;
}) {
  useGameOverlayPause();
  const { refresh } = useDirectMessages();
  const navigate = useNavigate(),
    location = useLocation();
  const [joining, setJoining] = useState<Invitation | null>(null);
  const [history, setHistory] = useState<History | null>(null),
    [text, setText] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [note, setNote] = useState(''),
    [block, setBlock] = useState(false);
  const alive = useRef(true),
    scroll = useRef<HTMLDivElement>(null),
    request = useRef(0),
    retry = useRef<{ body: string; clientId: string } | null>(null),
    lastRead = useRef(0);
  const load = async (before?: number) => {
    const seq = ++request.current;
    const next = await api<History>(`/messages/${person.id}${before ? `?before=${before}` : ''}`);
    if (!alive.current || seq !== request.current) return;
    const bottom =
      !scroll.current ||
      scroll.current.scrollHeight - scroll.current.scrollTop - scroll.current.clientHeight < 80;
    const oldHeight = scroll.current?.scrollHeight || 0;
    setHistory((old) =>
      before && old
        ? {
            ...next,
            messages: [
              ...next.messages,
              ...old.messages.filter((m) => !next.messages.some((n) => n.id === m.id)),
            ],
          }
        : old
          ? {
              ...next,
              next: old.next,
              messages: [
                ...old.messages.filter((m) => m.seq < (next.messages[0]?.seq || Infinity)),
                ...next.messages,
              ],
            }
          : next,
    );
    requestAnimationFrame(() => {
      if (scroll.current) {
        if (before) scroll.current.scrollTop += scroll.current.scrollHeight - oldHeight;
        else if (bottom) scroll.current.scrollTop = scroll.current.scrollHeight;
      }
    });
    const through = next.messages.at(-1)?.seq || 0;
    if (
      !before &&
      !document.hidden &&
      document.hasFocus() &&
      bottom &&
      through > lastRead.current
    ) {
      await api(`/messages/${person.id}/read`, json('POST', { through }));
      if (alive.current) {
        lastRead.current = through;
        await refresh();
      }
    }
  };
  useEffect(() => {
    alive.current = true;
    let pending = false;
    const read = () => {
      if (document.hidden || pending) return;
      pending = true;
      void load()
        .catch((e) => {
          if (alive.current) setError(e.message);
        })
        .finally(() => {
          pending = false;
        });
    };
    read();
    const timer = setInterval(read, 3000);
    window.addEventListener('focus', read);
    return () => {
      alive.current = false;
      clearInterval(timer);
      window.removeEventListener('focus', read);
    };
  }, [person.id]);
  const act = async (task: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      await task();
    } catch (e) {
      if (alive.current) setError((e as Error).message);
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const send = () =>
    void act(async () => {
      const body = text.trim();
      if (!body) return;
      const payload =
        retry.current?.body === body ? retry.current : { body, clientId: crypto.randomUUID() };
      retry.current = payload;
      await api(`/messages/${person.id}`, json('POST', payload));
      retry.current = null;
      if (!alive.current) return;
      setText('');
      await load();
      if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
      await refresh();
    });
  const canSend = history?.canSend && person.canSend;
  const joinInvite = (invite: Invitation, confirmed = false) => {
    if (location.pathname.startsWith('/play/') && !confirmed) {
      setJoining(invite);
      return;
    }
    void act(async () => {
      const result = await api<{ gameId: string; roomId: string }>(
        `/social/invites/${invite.id}/respond`,
        json('POST', { accept: true }),
      );
      if (!alive.current) return;
      setJoining(null);
      window.dispatchEvent(new Event('moyu:notifications-refresh'));
      await load();
      navigate(`/play/${result.gameId}?room=${result.roomId}`);
    });
  };
  const timeline = [
    ...(history?.messages || []).map((message) => ({
      id: message.id,
      at: message.createdAt,
      message,
      invite: null as Invitation | null,
    })),
    ...(history?.invitations || []).map((invite) => ({
      id: invite.id,
      at: invite.createdAt,
      message: null as Message | null,
      invite,
    })),
  ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  return (
    <section className="dm-chat" aria-label={`与${person.name}的私信`}>
      <header>
        {(fullPage || inGame) && (
          <button
            className={inGame ? 'dm-back dm-game-back' : 'dm-back'}
            aria-label={inGame ? '返回游戏' : '返回上一页'}
            onClick={close}
          >
            <ArrowLeft size={21} />
          </button>
        )}
        <img className="dm-coast" src="/art/chat-v1/coastal-header-simple.png" alt="" />
        <UserAvatar src={person.avatarUrl} size={50} />
        <div>
          <strong>{person.name}</strong>
          <small>好友私信</small>
        </div>
        <button title="更多操作" aria-label="更多操作" onClick={() => setBlock(!block)}>
          <Ellipsis size={20} />
        </button>
        {!fullPage && (
          <button
            className={inGame ? 'dm-game-close' : undefined}
            aria-label="关闭聊天"
            onClick={close}
          >
            <X size={19} />
          </button>
        )}
      </header>
      {block && (
        <div className="dm-warning">
          屏蔽后将解除好友关系，无法互发消息和邀请。
          <button
            disabled={busy}
            onClick={() =>
              void act(async () => {
                await api(`/social/blocks/${person.id}`, json('POST', { block: true }));
                await refresh();
                window.dispatchEvent(new Event('moyu:social-changed'));
                close();
              })
            }
          >
            确认屏蔽
          </button>
          <button onClick={() => setBlock(false)}>取消</button>
        </div>
      )}
      <div className="dm-messages" ref={scroll} aria-label="聊天记录">
        {history?.next && (
          <button
            className="dm-older"
            disabled={busy}
            onClick={() => void act(() => load(history.next!))}
          >
            查看更早消息
          </button>
        )}
        {!history && !error && <p className="dm-placeholder">正在加载…</p>}
        {history && !history.messages.length && !history.invitations?.length && (
          <p className="dm-placeholder">从一句“要一起玩吗？”开始</p>
        )}
        {timeline.map((item, index) => {
          const mine = (item.message?.sender || item.invite?.sender) === userId;
          const stamp =
            index === 0 || Date.parse(item.at) - Date.parse(timeline[index - 1].at) > 300000;
          const inv = item.invite;
          const statusText: Record<string, string> = {
            accepted: '已加入',
            declined: '已婉拒',
            revoked: '已撤回',
            expired: '邀请已过期',
            closed: '房间已结束',
            unavailable: '邀请已失效',
          };
          return (
            <Fragment key={item.id}>
              {stamp && (
                <time className="dm-time" dateTime={item.at}>
                  {new Date(item.at).toDateString() === new Date().toDateString()
                    ? '今天 '
                    : new Date(item.at).toLocaleDateString('zh-CN', {
                        month: 'numeric',
                        day: 'numeric',
                      }) + ' '}
                  {new Date(item.at).toLocaleTimeString('zh-CN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </time>
              )}
              <article className={`dm-message ${mine ? 'mine' : ''} ${inv ? 'has-invite' : ''}`}>
                {!mine && !inv && <UserAvatar src={person.avatarUrl} size={34} />}
                {item.message && <p>{item.message.body}</p>}
                {inv && (
                  <div className="dm-game-card">
                    <div className="dm-game-art">
                      <Gamepad2 size={30} />
                      {inv.coverImage && (
                        <img
                          src={inv.coverImage}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.hidden = true;
                          }}
                        />
                      )}
                    </div>
                    <div className="dm-game-info">
                      <strong>{inv.name}</strong>
                      <small>
                        {inv.players !== null && inv.capacity !== null
                          ? `好友房间 · ${inv.players}/${inv.capacity} 人`
                          : '好友游戏邀请'}
                      </small>
                      <button
                        disabled={busy || inv.status !== 'pending' || mine}
                        onClick={() => joinInvite(inv)}
                      >
                        <Gamepad2 size={15} />
                        {inv.status === 'pending'
                          ? mine
                            ? '邀请已发送'
                            : '加入游戏'
                          : statusText[inv.status] || '邀请已结束'}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            </Fragment>
          );
        })}
      </div>
      {joining && (
        <div className="dm-warning">
          将离开当前游戏，请确认进度已保存。
          <button disabled={busy} onClick={() => joinInvite(joining, true)}>
            确认加入
          </button>
          <button onClick={() => setJoining(null)}>取消</button>
        </div>
      )}
      {error && (
        <p className="dm-warning" role="alert">
          {error}
        </p>
      )}
      {note && (
        <p className="dm-note" role="status">
          {note}
        </p>
      )}
      <footer>
        <button
          className="dm-invite"
          disabled={busy || !canSend || !roomId}
          title={roomId ? '邀请加入当前房间' : '先在支持联机的游戏中加入房间'}
          onClick={() =>
            void act(async () => {
              await api('/social/invites', json('POST', { roomId, userId: person.id }));
              if (alive.current) {
                await load();
                setNote('游戏邀请已发送');
              }
            })
          }
        >
          <Gamepad2 size={16} />
          邀请一起玩
        </button>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <textarea
            aria-label="私信内容"
            placeholder={canSend ? '和好友聊两句…' : '仅好友之间可发送私信'}
            value={text}
            disabled={!canSend || busy}
            maxLength={1000}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (canSend && text.trim()) send();
              }
            }}
          />
          <button type="submit" disabled={!canSend || busy || !text.trim()} aria-label="发送私信">
            <Send size={18} />
          </button>
        </form>
        <small>Enter 发送</small>
      </footer>
    </section>
  );
}
